import { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./lib/api";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Finance from "./pages/Finance";
import Sales from "./pages/Sales";
import SalesOrderCard from "./pages/SalesOrderCard";
import CustomerCard from "./pages/CustomerCard";
import Purchasing from "./pages/Purchasing";
import PurchaseOrderCard from "./pages/PurchaseOrderCard";
import VendorCard from "./pages/VendorCard";
import Inventory from "./pages/Inventory";
import ItemCard from "./pages/ItemCard";
import Projects from "./pages/Projects";
import ProjectCard from "./pages/ProjectCard";
import StockTransfers from "./pages/StockTransfers";
import Reports from "./pages/Reports";
import Team from "./pages/Team";
import Accounts from "./pages/Accounts";
import Locations from "./pages/Locations";
import CompanyProfile from "./pages/CompanyProfile";
import SearchResults from "./pages/SearchResults";
import SetPassword from "./pages/SetPassword";
import ResetPassword from "./pages/ResetPassword";
import DataIntegrity from "./pages/DataIntegrity";
import BankAccounts from "./pages/BankAccounts";
import Reconciliation from "./pages/Reconciliation";
import Approvals from "./pages/Approvals";
import MyApprovals from "./pages/MyApprovals";
import DraftCleanup from "./pages/DraftCleanup";
import RoleMatrix from "./pages/RoleMatrix";
import AuditLog from "./pages/AuditLog";
import RateBoard from "./pages/RateBoard";
import PoApproval from "./pages/PoApproval";
import AccountApproval from "./pages/AccountApproval";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RequireCap } from "./components/Permission";
import { RemountOnId } from "./components/RecordNav";
import PostedDocCard from "./pages/PostedDocCard";
import "./App.css";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post(
          "/auth/session",
          {},
          { headers: { "X-Session-ID": sessionId } }
        );
        setUser(data);
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        const notice = e.response?.status === 403 ? `?notice=${encodeURIComponent(e.response?.data?.detail || "")}` : "";
        window.history.replaceState(null, "", "/login");
        navigate(`/login${notice}`, { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="overline-label mt-4">Signing you in…</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/po-approval/:token" element={<PoApproval />} />
      <Route path="/account-approval/:token" element={<AccountApproval />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<RequireCap cap="reports.view" what="the dashboard"><Dashboard /></RequireCap>} />
        <Route path="finance" element={<RequireCap cap="finance.view" what="Finance"><Finance /></RequireCap>} />
        <Route path="sales" element={<RequireCap cap="sales.view" what="Sales"><Sales /></RequireCap>} />
        <Route path="sales/doc/:id" element={<RequireCap cap="sales.view" what="sales documents"><RemountOnId><SalesOrderCard /></RemountOnId></RequireCap>} />
        <Route path="sales/customer/:id" element={<RequireCap cap="sales.view" what="customers"><RemountOnId><CustomerCard /></RemountOnId></RequireCap>} />
        <Route path="sales/posted-shipment/:id" element={<RequireCap cap="sales.view" what="posted shipments"><RemountOnId><PostedDocCard kind="shipment" /></RemountOnId></RequireCap>} />
        <Route path="sales/posted-return-receipt/:id" element={<RequireCap cap="sales.view" what="posted return receipts"><RemountOnId><PostedDocCard kind="returnReceipt" /></RemountOnId></RequireCap>} />
        <Route path="purchasing/posted-receipt/:id" element={<RequireCap cap="purchasing.view" what="posted receipts"><RemountOnId><PostedDocCard kind="receipt" /></RemountOnId></RequireCap>} />
        <Route path="purchasing" element={<RequireCap cap="purchasing.view" what="Purchasing"><Purchasing /></RequireCap>} />
        <Route path="purchasing/doc/:id" element={<RequireCap cap="purchasing.view" what="purchase documents"><RemountOnId><PurchaseOrderCard /></RemountOnId></RequireCap>} />
        <Route path="purchasing/vendor/:id" element={<RequireCap cap="purchasing.view" what="vendors"><RemountOnId><VendorCard /></RemountOnId></RequireCap>} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="inventory/item/:id" element={<RemountOnId><ItemCard /></RemountOnId>} />
        <Route path="projects" element={<RequireCap cap="projects.view" what="Projects"><Projects /></RequireCap>} />
        <Route path="projects/:id" element={<RequireCap cap="projects.view" what="Projects"><RemountOnId><ProjectCard /></RemountOnId></RequireCap>} />
        <Route path="stock-transfers" element={<RequireCap cap="projects.view" what="Stock Transfers"><StockTransfers /></RequireCap>} />
        <Route path="reports" element={<RequireCap cap="reports.view" what="Reports"><Reports /></RequireCap>} />
        <Route path="team" element={<Team />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="locations" element={<Locations />} />
        <Route path="company-profile" element={<CompanyProfile />} />
        <Route path="data-integrity" element={<DataIntegrity />} />
        <Route path="bank" element={<BankAccounts />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="my-approvals" element={<MyApprovals />} />
        <Route path="draft-cleanup" element={<DraftCleanup />} />
        <Route path="roles-matrix" element={<RoleMatrix />} />
        <Route path="audit-log" element={<AuditLog />} />
        <Route path="fx-rates" element={<RateBoard />} />
        <Route path="reconciliation" element={<RequireCap cap="sales.post" what="Bank Reconciliation"><Reconciliation /></RequireCap>} />
        <Route path="search" element={<SearchResults />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}
